import React from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  TextField,
  LinearProgress,
  Typography
} from '@material-ui/core';
import { useStyles } from './styles';
import { extractApiErrorMessage, notifications } from '../../utils';
import { useTranslation } from 'react-i18next';
import { LocationStableSelect, BlockColourPicker, MedicalSupplySelectModal } from '../../elements';
import { MedicalSupplyViewModel, DiseaseStableColour } from '../../models/injection';
import { ApiService } from '../../hooks';
import { LocationStable } from '../../models/locations';
import { HealthSystemDomainDiseasesCommandsCreateDiseaseCommand } from '../../api/HealthSystemApi';

interface CreateDiseaseModalProps {
  opened: boolean;
  onModalAction: (result: boolean) => void;
}

const CreateDiseaseModal: React.FC<any> = ({ opened, onModalAction }: CreateDiseaseModalProps) => {
  const classes = useStyles();
  const apiService = new ApiService();
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [stableColours, setStableColours] = React.useState([] as DiseaseStableColour[]);
  const [medicalSupply, setMedicalSupply] = React.useState(null as MedicalSupplyViewModel | null);

  const [name, setName] = React.useState('');
  const [daysToTreat, setDaysToTreat] = React.useState(0);
  const [dose, setDose] = React.useState(0);

  const getRandomColour = () => `#${getRandomInt(100000, 900000).toString()}`;

  const getRandomInt = (min: number, max: number) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min)) + min;
  };

  const closeDialog = (result: boolean) => {
    clearFields();
    onModalAction(result);
  };

  const handleCreate = () => {
    if (!validateForm()) {
      return;
    }

    setIsBusy(true);

    apiService.api.diseases
      .diseasesCreateDisease(buildCreateCommand())
      .then(() => {
        notifications.success(t('f-diseases-add-ok'));
        setIsBusy(false);
        clearFields();
        onModalAction(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        setIsBusy(false);
      });
  };

  const buildCreateCommand = (): HealthSystemDomainDiseasesCommandsCreateDiseaseCommand => {
    return {
      name,
      stableColours,
      // validateForm() (called before this, in handleCreate) already rejects a null medicalSupply.
      medicalSupplyId: medicalSupply!.id,
      daysToTreat,
      dose
    };
  };

  const clearFields = () => {
    setName('');
    setDaysToTreat(0);
    setDose(0);
    setMedicalSupply(null);
    setStableColours([]);
  };

  const validateForm = (): boolean => {
    if (!name) {
      notifications.warning(t('f-val-disease-name'));
      return false;
    }

    if (!medicalSupply) {
      notifications.warning(t('f-val-supply'));
      return false;
    }

    if (daysToTreat <= 0) {
      notifications.warning(t('f-val-disease-days'));
      return false;
    }

    if (dose <= 0) {
      notifications.warning(t('f-val-disease-dose'));
      return false;
    }

    return true;
  };

  const handleAddStableColourClick = () => {
    setStableColours([...stableColours, { colour: getRandomColour(), stable: LocationStable.Unknown }]);
  };

  const renderStableColoursSelectors = () => {
    return (
      <Grid item container spacing={1} direction="column">
        {stableColours.map((_value, index) => (
          <Grid item container alignItems="center" key={index}>
            <Grid item md={10}>
              <LocationStableSelect
                value={stableColours[index].stable}
                cssClass={classes.formControl}
                fullWidth
                allowAny
                onLocationStableChange={(value) => handleStableChange(value, index)}
              />
            </Grid>

            <Grid item md={2}>
              <BlockColourPicker
                colour={stableColours[index].colour}
                handleColourChange={(colour: any) => handleStableColourChange(colour, index)}
              />
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  };

  const handleStableChange = (value: number, index: number) => {
    stableColours[index].stable = value;
    setStableColours([...stableColours]);
  };

  const handleStableColourChange = (colour: any, index: number) => {
    stableColours[index].colour = colour.hex;
    setStableColours([...stableColours]);
  };

  return (
    <Dialog
      id="createDiseadeDialog"
      open={opened}
      onClose={() => closeDialog(false)}
      className={classes.dialog}
      maxWidth="md"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="createDiseadeDialogTitle" className={classes.dialogHeaderFooter}>
        {t('f-diseases-add-title')}
      </DialogTitle>

      <DialogContent>
        <Grid item container md={12} direction="column">
          <Grid item>
            <TextField
              label={t('f-com-name')}
              size="small"
              fullWidth
              variant="outlined"
              className={classes.formControl}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </Grid>

          <Grid item>
            <Typography variant="h6" className={classes.formControl}>
              {t('f-diseases-colour')}
            </Typography>
          </Grid>

          {renderStableColoursSelectors()}

          <Grid item>
            <Button
              id="addStableColBtn"
              onClick={handleAddStableColourClick}
              size="small"
              startIcon={<Icon>{'add_circle_outline'}</Icon>}
              color="primary"
            >
              {t('f-diseases-colour-add')}
            </Button>
          </Grid>

          <Grid item>
            <Typography variant="h6" className={classes.formControl}>
              {t('f-com-treat')}
            </Typography>
          </Grid>

          <Grid item>
            <MedicalSupplySelectModal
              disableAny
              supply={medicalSupply}
              sx={{ m: 1 }}
              onChange={(supply) => setMedicalSupply(supply)}
            />
          </Grid>

          <Grid item>
            <TextField
              label={t('f-com-treat-num-days')}
              variant="outlined"
              className={classes.formControl}
              value={daysToTreat}
              size="small"
              type="number"
              onChange={(e) => {
                const newValue = Number(e.target.value);
                if (newValue >= 0) {
                  setDaysToTreat(newValue);
                }
              }}
            />
          </Grid>

          <Grid item>
            <TextField
              label={t('f-com-dose')}
              variant="outlined"
              className={classes.formControl}
              value={dose}
              size="small"
              type="number"
              onChange={(e) => {
                const newValue = Number(e.target.value);
                if (newValue >= 0) {
                  setDose(newValue);
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogHeaderFooter}>
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <Button
              onClick={() => closeDialog(false)}
              variant="outlined"
              disabled={isBusy}
              className={classes.formControl}
              startIcon={<Icon>{'close'}</Icon>}
              color="primary"
            >
              {t('f-com-cancel')}
            </Button>

            <Button
              onClick={handleCreate}
              id="createBtn"
              disabled={isBusy}
              className={classes.formControl}
              startIcon={<Icon>{'add_circle_outline'}</Icon>}
              variant="contained"
              color="primary"
            >
              {t('f-com-create')}
            </Button>
          </Grid>

          {isBusy && (
            <Grid item>
              <LinearProgress />
            </Grid>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDiseaseModal;
